import {
  Component,
  ComponentInterface,
  Element,
  forceUpdate,
  FunctionalComponent,
  h,
  Host,
  Method,
  Prop,
  State,
  VNode,
  writeTask,
} from "@stencil/core"
import { FP, RemoteData, Rx, RxOperators, Swiper } from "../../deps"

import * as Errors from "../../errors"
import * as Events from "../../events/events"
import * as SellalongService from "../../service-sellalong/service-sellalong"
import {
  MonetaryAmount,
  Purchase,
} from "../../service-sellalong/service-sellalong"
import * as Theme from "../../theme/theme"

@Component({
  tag: "sellalong-panel",
  scoped: true,
  styleUrls: {
    theme_8629350: "./sellalong-panel-theme-sirplus.css",
    theme_27219324: "./sellalong-panel-theme-bmo.css",
    theme_53156151463: "./sellalong-panel.css",
  },
})
export class Panel implements ComponentInterface {
  @Element()
  element!: HTMLElement

  private theme!: Theme.Theme

  @State()
  swiper: Swiper.Swiper | null = null

  /**
   * Whether to show logged out variant of panel - shows sample items from theme
   */
  @Prop()
  showLoggedOut: boolean = false

  /**
   * Purchases to render in the panel
   */
  @Prop()
  purchases: RemoteData.RemoteData<Error, readonly Purchase[]> =
    RemoteData.initial

  /**
   * Current sellalong discount amount applied to the cart
   */
  @Prop()
  discountAmount: MonetaryAmount | null = null

  @Prop()
  cartItemCount: number = 0

  /**
   * Current total price of cart
   */
  @Prop()
  cartTotal: MonetaryAmount | null = null

  /**
   * Current cart total after discount is applied
   */
  @Prop()
  cartTotalAfterDiscount: MonetaryAmount | null = null

  /**
   * Rolled-over credit
   */
  @Prop()
  rolledOverCredit: MonetaryAmount | null = null

  /**
   * Display variant for the panel.
   */
  @Prop()
  variant: "mini" | "full" = "full"

  @State()
  email: string = ""

  onCheckoutIntent = Events.createPublishCallback({
    kind: "checkoutIntent",
    originSection: "sellalongPanel",
  })

  onEmailEntryAction = (value: string): void => {
    this.email = value
  }

  onEmailEntryIntent = Events.createPublishCallback({
    kind: "loginEmailEntryIntent",
  })

  onStartLoginIntent = () =>
    Events.publish({
      kind: "loginStartIntent",
      email: this.email,
    })

  onHelpModalShowIntent = Events.createPublishCallback({
    kind: "helpModalShowIntent",
  })

  async componentWillLoad() {
    this.theme = await Theme.loadTheme(this.element)()

    if (window.IntersectionObserver) {
      const options: IntersectionObserverInit = {
        root: null,
        threshold: 0.5,
      }

      FP.function
        .pipe(
          new Rx.Observable<IntersectionObserverEntry[]>((subscriber) => {
            const observer = new IntersectionObserver(
              (entries) => subscriber.next(entries),
              options
            )

            observer.observe(this.element)
          }),
          RxOperators.map(
            FP.readonlyArray.findFirst((entry) => entry.isIntersecting)
          ),
          RxOperators.filter(FP.option.isSome),
          RxOperators.take(1),
          RxOperators.tap(
            Events.createPublishCallback({ kind: "panelImpressionAction" })
          )
        )
        .subscribe()
    }

    if (window.location.hash === "#sellalong") {
      setTimeout(() => this.scrollToElement(), 250)
    }
  }

  disconnectedCallback() {
    Events.publish({ kind: "sellalongComponentRemovedInternal" })
  }

  @Method()
  async scrollToElement() {
    if (this.variant === "mini") {
      return
    }

    this.element.scrollIntoView({ behavior: "smooth" })
    if (window.location.hash !== "#sellalong") {
      setTimeout(() => (window.location.hash = "sellalong"), 500)
    }
  }

  getPurchases(): ReadonlyArray<Purchase> {
    return FP.function.pipe(
      this.purchases,
      RemoteData.toOption,
      FP.option.getOrElseW(() => this.theme.settings.samplePurchases)
    )
  }

  isLoading(): boolean {
    return (
      !this.showLoggedOut &&
      !RemoteData.isSuccess(this.purchases) &&
      !RemoteData.isFailure(this.purchases)
    )
  }

  getError(): FP.option.Option<Error> {
    return FP.function.pipe(
      this.purchases,
      FP.option.fromPredicate(RemoteData.isFailure),
      FP.option.map((data) => data.error)
    )
  }

  render() {
    const { settings, text } = this.theme
    const {
      cartItemCount,
      cartTotal,
      cartTotalAfterDiscount,
      discountAmount,
      email,
      initSwiper,
      rolledOverCredit,
      showLoggedOut,
      swiper,
      theme,
    } = this

    const hostClasses: Record<string, boolean> = {
      [`sellalong-panel-variant-${this.variant}`]: true,
      "sellalong-panel--cart-empty": cartItemCount === 0,
    }

    const purchases = this.getPurchases()

    const isLoading = this.isLoading()

    const error = this.getError()

    const isCustomerNotFoundError =
      FP.option.isSome(error) && Errors.isCustomerNotFoundError(error.value)

    const errorFragment = isLoading ? (
      <DisplayError message="Loading..." />
    ) : isCustomerNotFoundError ? (
      <DisplayError message="We could not load your purchases, please enter your email and try again" />
    ) : FP.option.isSome(error) ? (
      <DisplayError message={error.value.message} type="unexpected" />
    ) : null

    const loggedOutFragment: VNode =
      showLoggedOut || isCustomerNotFoundError ? (
        <LoginCTA
          email={email}
          onEmailEntryIntent={this.onEmailEntryIntent}
          onEmailEntryAction={this.onEmailEntryAction}
          onStartIntent={this.onStartLoginIntent}
          theme={theme}
        />
      ) : null

    const bodyFragment: VNode[] = [
      <div class="swiper swiper-container" ref={initSwiper}>
        <Navigation
          hide={showLoggedOut}
          hideNavAtEnds={!settings.panelNavCentered}
          swiper={swiper}
        />

        {loggedOutFragment}

        <ol class="sellalong-panel-purchase-list swiper-wrapper">
          {purchases.map((purchase) => (
            <li
              class="sellalong-panel-purchase-item swiper-slide"
              key={purchase.id}
            >
              <sellalong-panel-purchase-item
                purchase={purchase}
                variant={this.variant}
              />
            </li>
          ))}
        </ol>
      </div>,
      <Footer
        cartTotal={cartTotal}
        cartTotalAfterDiscount={cartTotalAfterDiscount}
        discountAmount={discountAmount}
        onCheckoutIntent={this.onCheckoutIntent}
        purchases={purchases}
        rolledOverCredit={rolledOverCredit}
        theme={this.theme}
        variant={this.variant}
      />,
    ]

    const stepsFragment: VNode =
      this.variant === "full" ? (
        <ol class="sellalong-panel-steps">
          <li>{text("panelStep1")}</li>
          <li>{text("panelStep2")}</li>
          <li>{text("panelStep3")}</li>
        </ol>
      ) : null

    // ensure swiper updates after a render - eg: login changes items
    if (swiper !== null) {
      writeTask(() => swiper.update())
    }

    return (
      <Host id="sellalong" class={hostClasses}>
        <section>
          <h3 class="sellalong-panel-heading">
            {text("panelTitle")}
            <a
              class="sellalong-panel-help-cta"
              href="#sellalong-help"
              title="Help"
              onClick={this.onHelpModalShowIntent}
            >
              <HelpIcon />
            </a>
          </h3>
          {stepsFragment}
          {errorFragment}
          {bodyFragment}
        </section>
      </Host>
    )
  }

  initSwiper = (element?: HTMLElement) => {
    // Swiper dom element has changed
    if (element && this.swiper !== null && this.swiper.el !== element) {
      this.swiper.destroy()
      this.swiper = null
    }

    if (this.swiper === null && element) {
      const {
        theme: { settings },
        variant,
      } = this

      writeTask(() => {
        Swiper.Swiper.use([Swiper.Mousewheel])
        this.swiper = new Swiper.Swiper(element, {
          enabled: true,
          slidesPerView: variant === "full" ? "auto" : 2,
          spaceBetween:
            variant === "full" ? settings.panelItemSpaceBetween : 10,
          centeredSlides: settings.panelNavCentered,
          centeredSlidesBounds: settings.panelNavCentered,
          mousewheel: {
            forceToAxis: true,
            thresholdDelta: 3,
          },
        })
        this.swiper.on("slideChange", () => forceUpdate(this))
        forceUpdate(this)
      })
    }
  }
}

const DisplayError: FunctionalComponent<{
  message: string
  type?: "unexpected"
}> = ({ message, type }) => (
  <div class="sellalong-panel-error">
    {type === "unexpected" ? (
      <p>
        An unexpected error occurred, please refresh the page or try again later
      </p>
    ) : null}
    <p>{message}</p>
  </div>
)

const LeftNavIcon: FunctionalComponent = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
  >
    <title>Left</title>
    <polyline points="15 18 9 12 15 6"></polyline>
  </svg>
)

const RightNavIcon: FunctionalComponent = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
  >
    <title>Right</title>
    <polyline points="9 18 15 12 9 6"></polyline>
  </svg>
)

function Navigation({
  hide,
  hideNavAtEnds,
  swiper,
}: {
  hide: boolean
  hideNavAtEnds: boolean
  swiper: Swiper.Swiper | null
}) {
  if (!swiper) {
    return null
  }

  const classes = {
    "sellalong-panel-nav": true,
    "sellalong-panel-nav--hide": hide,
    "sellalong-panel-nav--start": hideNavAtEnds && swiper.isBeginning,
    "sellalong-panel-nav--end": hideNavAtEnds && swiper.isEnd,
  }

  return (
    <div class={classes}>
      <a
        class="sellalong-panel-nav-prev"
        href="#"
        onClick={(ev) => {
          ev.preventDefault()
          swiper.slidePrev()
        }}
      >
        <LeftNavIcon />
      </a>
      <a
        class="sellalong-panel-nav-next"
        href="#"
        onClick={(ev) => {
          ev.preventDefault()
          swiper.slideNext()
        }}
      >
        <RightNavIcon />
      </a>
    </div>
  )
}

type FooterProps = {
  cartTotal: SellalongService.MonetaryAmount | null
  cartTotalAfterDiscount: SellalongService.MonetaryAmount | null
  discountAmount: SellalongService.MonetaryAmount | null
  onCheckoutIntent: () => void
  purchases: ReadonlyArray<Purchase>
  rolledOverCredit: SellalongService.MonetaryAmount | null
  theme: Theme.Theme
  variant: Panel["variant"]
}
const Footer: FunctionalComponent<FooterProps> = (props) => {
  const {
    cartTotal,
    cartTotalAfterDiscount,
    discountAmount,
    onCheckoutIntent,
    purchases,
    rolledOverCredit,
    theme,
    variant,
  } = props
  const { settings, text } = theme
  const isFull = variant === "full"

  const { selectedCount, selectedAmount } = FP.function.pipe(
    purchases,
    FP.readonlyArray.reduce(
      {
        selectedCount: 0,
        selectedAmount: {
          currency: "GBP",
          value: 0,
        },
      },
      (acc, curr) =>
        curr.state === SellalongService.PurchaseState.ToSell
          ? {
              selectedCount: acc.selectedCount + 1,
              selectedAmount: {
                currency: acc.selectedAmount.currency,
                value:
                  acc.selectedAmount.value + (curr.offer.price?.value || 0),
              },
            }
          : acc
    )
  )

  const cartOverflowCredit =
    (cartTotal?.value ?? 0) - (discountAmount?.value ?? 0)

  // dynamically create checkout element based on theme settings
  const checkoutElem = h(
    settings.panelCheckoutElement,
    {
      class: FP.function.pipe(
        settings.panelCheckoutClasses,
        FP.readonlyArray.reduce({} as Record<string, boolean>, (acc, curr) =>
          FP.readonlyRecord.upsertAt(curr, true)(acc)
        )
      ),
      type: "submit",
      name: "checkout",
      value:
        settings.panelCheckoutElement === "input"
          ? text("panelFooterCheckoutCTA")
          : null,
      onClick: onCheckoutIntent,
    },
    settings.panelCheckoutElement === "button"
      ? text("panelFooterCheckoutCTA")
      : ""
  )

  const continueShoppingClasses = FP.function.pipe(
    settings.panelContinueShoppingClasses,
    FP.readonlyArray.reduce(
      {
        "sellalong-panel-continue-shopping": true,
      } as Record<string, boolean>,
      (acc, curr) => FP.readonlyRecord.upsertAt(curr, true)(acc)
    )
  )

  return (
    <div class="sellalong-panel-footer">
      {isFull ? <sellalong-powered-by /> : null}
      <div class="sellalong-panel-footer__right">
        <div class="sellalong-panel-status">
          {text("panelFooterStatus")({
            credit: selectedAmount,
            selectedCount,
          })}
          <br />
          {text("panelFooterRolledOver")({
            rolledOverCredit,
          })}
        </div>
        {isFull ? (
          <sellalong-discount-display
            discountAmount={discountAmount}
            cartTotal={cartTotal}
            cartTotalAfterDiscount={cartTotalAfterDiscount}
          />
        ) : null}
        {cartOverflowCredit < 0 ? (
          <div class="sellalong-panel-checkout-note">
            {text("panelFooterAdditionalCredit")({
              credit: {
                currency: "GBP",
                value: Math.abs(cartOverflowCredit),
              },
            })}
          </div>
        ) : null}

        {isFull ? (
          <div class="sellalong-panel-footer__checkout-buttons">
            {checkoutElem}
          </div>
        ) : null}

        {isFull ? (
          <a class={continueShoppingClasses} href="/">
            {text("panelFooterContinueShopping")}
          </a>
        ) : null}
      </div>
    </div>
  )
}

const HelpIcon: FunctionalComponent = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <g fill="none" fill-rule="evenodd">
      <g>
        <g transform="translate(-928.000000, -151.000000) translate(928.000000, 151.000000)">
          <circle cx="12" cy="12" r="11.6" stroke="#000" stroke-width=".8" />
          <path
            fill="#000"
            fill-rule="nonzero"
            d="M12.092 14.578c0-2.468 3.642-2.837 3.642-5.774 0-2.098-1.644-3.088-3.709-3.088-2.417 0-3.877 1.712-3.877 2.77 0 .486.32.755.79.755 1.107 0 .754-2.837 3.154-2.837 1.074 0 2.149.806 2.149 2.25 0 2.684-3.542 3.12-3.256 5.924.1.436 1.107.42 1.107 0zm-.537 3.407c.57 0 1.024-.336 1.024-.957 0-.655-.453-.99-1.024-.99-.587 0-1.04.335-1.04.99 0 .621.453.957 1.04.957z"
          />
        </g>
      </g>
    </g>
  </svg>
)

type LoginCTAProps = {
  email: string
  onEmailEntryIntent: () => void
  onEmailEntryAction: (value: string) => void
  onStartIntent: () => void
  theme: Theme.Theme
}
const LoginCTA: FunctionalComponent<LoginCTAProps> = ({
  email,
  onEmailEntryIntent,
  onEmailEntryAction,
  onStartIntent,
  theme: { text },
}) => (
  <div class="sellalong-panel-login">
    <form
      class="Form"
      onSubmit={(event) => {
        event.preventDefault()
        onStartIntent()
      }}
    >
      <div class="Form__Row">
        <div class="Form__Column">
          <input
            class="sellalong-panel-login-email Form__Input"
            aria-label="Email address"
            type="email"
            placeholder="Enter your email to start"
            onFocus={onEmailEntryIntent}
            onInput={(event: Event) =>
              onEmailEntryAction((event.target as any).value || "")
            }
            value={email}
          />
        </div>
        <div class="Form__Column">
          <button
            class="sellalong-panel-login-start-cta Button Button--primary"
            type="submit"
          >
            Start
          </button>
        </div>
      </div>
    </form>

    {/* <a
      href="/account/login?return_url=%2fcart"
      onClick={Events.createPublishCallback({ kind: "loginCtaIntent" })}
    >
      {text("panelLoginCTA")}
    </a> */}
    <p>{text("panelLoginSubtitle")}</p>
  </div>
)
